import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  DataUsageOutlined,
  EventAvailableOutlined,
  PaidOutlined,
  ScheduleOutlined,
  TableRowsOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import API from '../data/api';
import CustomTextField from '../components/custom/CustomTextField';
import ForgotPassword from '../sections/auth/login/ForgotPassword';

const buildProfileValues = (userInfo = {}) => ({
  firstName: userInfo.given_name || '',
  lastName: userInfo.family_name || '',
  username: userInfo.preferred_username || '',
  email: userInfo.email || '',
  phoneNumber: userInfo.phone_number || '',
  jobTitle: userInfo.position || '',
});

const readStoredUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem('user_info') || '{}');
  } catch (error) {
    console.error('Failed to parse user_info from localStorage:', error);
    return {};
  }
};

const DEFAULT_NOTIFICATION_PREFERENCES = [
  {
    id: 'security_alerts',
    code: 'security_alerts',
    title: 'Security alerts',
    description: 'Important updates about account access and security activity.',
    is_enabled: true,
    updated_at: null,
  },
  {
    id: 'billing_invoice_updates',
    code: 'billing_invoice_updates',
    title: 'Billing and invoice updates',
    description: 'Updates related to invoices, billing cycles, and payments.',
    is_enabled: true,
    updated_at: null,
  },
  {
    id: 'weekly_performance_insights',
    code: 'weekly_performance_insights',
    title: 'Weekly performance insights',
    description: 'A weekly summary of product and operational activity.',
    is_enabled: false,
    updated_at: null,
  },
  {
    id: 'product_announcements',
    code: 'product_announcements',
    title: 'Product announcements',
    description: 'News about product releases, enhancements, and launches.',
    is_enabled: false,
    updated_at: null,
  },
];

const formatUpdatedAt = (value) => {
  if (!value) {
    return 'Never';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Never';
  }
  return parsed.toLocaleString();
};

const normalizeDepotId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : value;
};

const getPreferenceDepotId = (preference = {}, fallbackDepotId = null) => {
  const activeScopeDepotId = Array.isArray(preference?.scopes)
    ? preference.scopes.find((scope) => scope && scope.is_active !== false && scope.depot_id !== null && scope.depot_id !== undefined)
      ?.depot_id
    : null;

  const firstScopeDepotId = Array.isArray(preference?.scopes)
    ? preference.scopes.find((scope) => scope && scope.depot_id !== null && scope.depot_id !== undefined)?.depot_id
    : null;

  return normalizeDepotId(preference?.depot_id ?? activeScopeDepotId ?? firstScopeDepotId ?? fallbackDepotId);
};

const buildPreferenceCode = (preference = {}) => {
  if (preference?.code) {
    return String(preference.code);
  }

  if (preference?.title) {
    return String(preference.title)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  const identifier = preference?.notification_preference_id ?? preference?.id;
  return identifier !== undefined && identifier !== null ? `preference_${identifier}` : '';
};

const buildPreferenceToggleKey = (preference = {}) => {
  const identifier = preference?.notification_preference_id ?? preference?.id;
  if (identifier !== undefined && identifier !== null && identifier !== '') {
    return String(identifier);
  }
  return buildPreferenceCode(preference);
};

const SUPPORT_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const SUPPORT_TAG_OPTIONS = [
  'Bug',
  'Feature request',
  'Performance',
  'Billing',
  'Data issue',
  'Integration',
  'UI/UX',
  'Access',
];

const INITIAL_SUPPORT_TICKET = {
  title: '',
  description: '',
  priority: 'medium',
  category: '',
};

const toTitleCase = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const humanizeDatasetName = (value = '') =>
  toTitleCase(
    String(value || '')
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/orderitems/gi, 'order items')
      .replace(/suborder_metadata/gi, 'suborder metadata')
      .replace(/orderitem_metadata/gi, 'orderitem metadata')
      .replace(/product_details/gi, 'product details')
      .replace(/mappop/gi, 'mapped proof of payment'),
  );

const DATA_EXPORT_ROW_RATE_USD = 0.0025;

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const formatExportDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const formatBytes = (bytes = 0) => {
  const normalized = Number(bytes) || 0;
  if (normalized <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(normalized) / Math.log(1024)), units.length - 1);
  const value = normalized / 1024 ** exponent;
  const precision = exponent === 0 ? 0 : 2;
  return `${value.toFixed(precision)} ${units[exponent]}`;
};

const findDateRange = (values = []) => {
  const timestamps = values
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((value) => Number.isFinite(value));

  if (timestamps.length === 0) {
    return { first: null, last: null };
  }

  const firstTimestamp = Math.min(...timestamps);
  const lastTimestamp = Math.max(...timestamps);
  return {
    first: new Date(firstTimestamp).toISOString(),
    last: new Date(lastTimestamp).toISOString(),
  };
};

const calculateExportCharge = (rowCount = 0) => rowCount * DATA_EXPORT_ROW_RATE_USD;

const isDateFieldName = (key = '') => /(_at|_date|date_|time|timestamp)/i.test(key);

const normalizeDateValue = (value) => {
  if (!value && value !== 0) {
    return null;
  }

  if (typeof value === 'number') {
    const normalizedTimestamp =
      value > 100000000000
        ? value
        : value > 1000000000
          ? value * 1000
          : NaN;

    if (!Number.isFinite(normalizedTimestamp)) {
      return null;
    }

    const parsed = new Date(normalizedTimestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  return null;
};

const buildTableDateRange = (rows = []) => {
  const dates = [];

  rows.forEach((row) => {
    Object.entries(row || {}).forEach(([key, value]) => {
      if (!isDateFieldName(key)) {
        return;
      }

      const normalized = normalizeDateValue(value);
      if (normalized) {
        dates.push(normalized);
      }
    });
  });

  return findDateRange(dates);
};

const toPositiveNumber = (value) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized < 0) {
    return 0;
  }
  return normalized;
};

const parseBundleDataset = (dataset = null) => {
  if (Array.isArray(dataset)) {
    return {
      rows: dataset,
      row_count: dataset.length,
    };
  }

  if (dataset && typeof dataset === 'object') {
    const rows = Array.isArray(dataset?.data) ? dataset.data : [];
    return {
      rows,
      row_count: toPositiveNumber(
        dataset?.row_count ?? dataset?.rows_count ?? dataset?.total_rows ?? rows.length,
      ),
    };
  }

  return {
    rows: [],
    row_count: 0,
  };
};

const extractBundleDownloadUrl = (bundlePayload = {}, manifestPayload = {}) => {
  const normalizeUrl = (value) => {
    if (typeof value !== 'string') {
      return '';
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : '';
  };

  const topLevelCandidates = [
    bundlePayload?.download_url,
    bundlePayload?.download_link?.link,
    manifestPayload?.download_url,
    manifestPayload?.download_link?.link,
  ];

  for (const candidate of topLevelCandidates) {
    const normalized = normalizeUrl(candidate);
    if (normalized) {
      return normalized;
    }
  }

  const datasetObjects = Object.values(bundlePayload?.datasets || {});
  for (const datasetObject of datasetObjects) {
    const normalized = normalizeUrl(datasetObject?.download_link?.link);
    if (normalized) {
      return normalized;
    }
  }

  return '';
};

const buildExportTables = (manifestRows = [], bundleDatasets = {}) => {
  const manifestMap = new Map(
    (Array.isArray(manifestRows) ? manifestRows : []).map((dataset) => [
      dataset?.dataset_name,
      dataset,
    ]),
  );

  // Only render tables that exist in the bundle payload.
  const datasetNames = Object.keys(bundleDatasets || {}).filter(Boolean);

  return datasetNames.map((datasetName) => {
    const manifest = manifestMap.get(datasetName);
    const parsedBundleDataset = parseBundleDataset(bundleDatasets?.[datasetName]);
    const datasetRows = parsedBundleDataset.rows;
    const dateRange = buildTableDateRange(datasetRows);
    const rowCount = manifest
      ? toPositiveNumber(manifest?.row_count)
      : toPositiveNumber(parsedBundleDataset.row_count);

    return {
      table_name: datasetName,
      row_count: rowCount,
      loaded_rows: datasetRows.length,
      first_inserted_at: dateRange.first,
      last_inserted_at: dateRange.last,
      is_loaded: datasetRows.length > 0,
    };
  });
};

export default function SettingsPage() {
  const [profileValues, setProfileValues] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    jobTitle: '',
  });
  const [initialProfileValues, setInitialProfileValues] = useState(profileValues);
  const [notificationPrefs, setNotificationPrefs] = useState(DEFAULT_NOTIFICATION_PREFERENCES);
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(false);
  const [notificationPrefsSaving, setNotificationPrefsSaving] = useState(false);
  const [notificationPrefsError, setNotificationPrefsError] = useState('');
  const [notificationPrefsSuccess, setNotificationPrefsSuccess] = useState('');
  const [uiPrefs, setUiPrefs] = useState({
    timezone: 'Africa/Johannesburg',
    language: 'en',
    dateFormat: 'dd/MM/yyyy',
    compactTables: false,
  });
  const [accountMeta, setAccountMeta] = useState({
    name: '',
    email: '',
    role: '',
    companyName: '',
    companyId: '',
    registrationId: '',
  });
  const [accountMetaError, setAccountMetaError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [openSupportModal, setOpenSupportModal] = useState(false);
  const [supportTicket, setSupportTicket] = useState({ ...INITIAL_SUPPORT_TICKET });
  const [supportTicketErrors, setSupportTicketErrors] = useState({});
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportSubmitSuccess, setSupportSubmitSuccess] = useState('');
  const [supportSubmitError, setSupportSubmitError] = useState('');
  const [openPasswordResetModal, setOpenPasswordResetModal] = useState(false);
  const [openDataExportModal, setOpenDataExportModal] = useState(false);
  const [dataExportLoading, setDataExportLoading] = useState(false);
  const [dataExportSummary, setDataExportSummary] = useState(null);
  const [dataExportDownloadUrl, setDataExportDownloadUrl] = useState('');
  const [dataExportError, setDataExportError] = useState('');
  const [dataExportUrlCopied, setDataExportUrlCopied] = useState(false);
  const [dataExportCarouselPage, setDataExportCarouselPage] = useState(0);

  useEffect(() => {
    const storedUserInfo = readStoredUserInfo();
    const initialValues = buildProfileValues(storedUserInfo);
    setProfileValues(initialValues);
    setInitialProfileValues(initialValues);
    const fallbackName = `${storedUserInfo?.given_name || ''} ${storedUserInfo?.family_name || ''}`.trim();
    setAccountMeta({
      name: storedUserInfo?.name || fallbackName || '',
      email: storedUserInfo?.email || '',
      role: Array.isArray(storedUserInfo?.roles) ? storedUserInfo.roles.join(', ') : storedUserInfo?.role || '',
      companyName: storedUserInfo?.company_name || '',
      companyId: storedUserInfo?.company_id ? String(storedUserInfo.company_id) : '',
      registrationId: storedUserInfo?.registration_id ? String(storedUserInfo.registration_id) : '',
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchUserDetails = async () => {
      setAccountMetaError('');
      try {
        const response = await API.get('settings/user-details');
        const user = response?.data?.user || {};
        const roles = Array.isArray(user?.roles) ? user.roles.join(', ') : user?.role || '';

        if (mounted) {
          setAccountMeta((previous) => ({
            ...previous,
            name: user?.name || previous.name,
            email: user?.email || previous.email,
            role: roles || previous.role,
            companyName: user?.company_name || previous.companyName,
            companyId: user?.company_id ? String(user.company_id) : previous.companyId,
            registrationId: user?.registration_id
              ? String(user.registration_id)
              : previous.registrationId,
          }));
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        if (mounted) {
          setAccountMetaError('Unable to refresh account snapshot. Showing latest cached values.');
        }
      }
    };

    fetchUserDetails();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchNotificationPreferences = async () => {
      setNotificationPrefsLoading(true);
      setNotificationPrefsError('');
      setNotificationPrefsSuccess('');

      try {
        const response = await API.get('settings/notification_preferences');
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];

        const normalizedRows =
          rows.length > 0
            ? rows.map((row) => ({
                id: row?.notification_preference_id || row?.id || row?.code,
                notification_preference_id: row?.notification_preference_id ?? row?.id ?? null,
                code: buildPreferenceCode(row),
                toggle_key: buildPreferenceToggleKey(row),
                title: row?.title || row?.code || 'Preference',
                description: row?.description || '',
                is_enabled: Boolean(row?.is_enabled),
                updated_at: row?.updated_at || null,
                depot_id: getPreferenceDepotId(row),
                scopes: Array.isArray(row?.scopes) ? row.scopes : [],
              }))
            : DEFAULT_NOTIFICATION_PREFERENCES;

        if (mounted) {
          setNotificationPrefs(normalizedRows);
        }
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        if (mounted) {
          setNotificationPrefs(DEFAULT_NOTIFICATION_PREFERENCES);
          setNotificationPrefsError('Failed to load notification preferences. Showing defaults.');
        }
      } finally {
        if (mounted) {
          setNotificationPrefsLoading(false);
        }
      }
    };

    fetchNotificationPreferences();

    return () => {
      mounted = false;
    };
  }, []);

  const hasProfileChanges = useMemo(
    () =>
      JSON.stringify(profileValues) !== JSON.stringify(initialProfileValues),
    [profileValues, initialProfileValues],
  );

  const dataExportCards = useMemo(() => {
    if (!dataExportSummary) {
      return [];
    }

    return [
      {
        key: 'first_inserted_at',
        label: 'First Data Insert',
        value: formatExportDateTime(dataExportSummary.firstInsertedAt),
        icon: EventAvailableOutlined,
      },
      {
        key: 'last_inserted_at',
        label: 'Last Data Insert',
        value: formatExportDateTime(dataExportSummary.lastInsertedAt),
        icon: ScheduleOutlined,
      },
      {
        key: 'total_rows',
        label: 'Total Rows',
        value: integerFormatter.format(dataExportSummary.totalRows),
        icon: TableRowsOutlined,
      },
      {
        key: 'data_size',
        label: 'Data Size',
        value: formatBytes(dataExportSummary.dataSizeBytes),
        icon: DataUsageOutlined,
      },
      {
        key: 'charge',
        label: 'Charge',
        value: usdFormatter.format(dataExportSummary.estimatedChargeUsd),
        icon: PaidOutlined,
      },
    ];
  }, [dataExportSummary]);

  const dataExportTablePages = useMemo(() => {
    const tables = Array.isArray(dataExportSummary?.tables) ? dataExportSummary.tables : [];
    const cardsPerView = 3;
    const pages = [];

    for (let index = 0; index < tables.length; index += cardsPerView) {
      pages.push(tables.slice(index, index + cardsPerView));
    }

    return pages;
  }, [dataExportSummary]);

  const currentDataExportPageIndex = Math.min(
    dataExportCarouselPage,
    Math.max(dataExportTablePages.length - 1, 0),
  );

  const visibleDataExportTables = dataExportTablePages[currentDataExportPageIndex] || [];

  useEffect(() => {
    setDataExportCarouselPage(0);
  }, [openDataExportModal, dataExportSummary?.tables?.length]);

  const handleProfileFieldChange = (event) => {
    setProfileValues((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  };

  const handleNotificationToggle = (event, incomingToggleKey = null) => {
    const { name, checked } = event.target;
    const toggleKey = String(incomingToggleKey ?? name ?? '');
    const currentPreferences = Array.isArray(notificationPrefs) ? notificationPrefs : [];
    const selectedPreference = currentPreferences.find(
      (pref) => String(pref?.toggle_key ?? buildPreferenceToggleKey(pref)) === toggleKey,
    );
    const preferenceIdentifier =
      selectedPreference?.notification_preference_id ?? selectedPreference?.id;
    if (!preferenceIdentifier) {
      setNotificationPrefsError('Notification preference cannot be updated right now.');
      return;
    }

    const preferenceId = Number(preferenceIdentifier);
    if (!Number.isFinite(preferenceId)) {
      setNotificationPrefsError('Notification preference ID is invalid.');
      return;
    }

    const storedUserInfo = readStoredUserInfo();
    const fallbackDepotId = normalizeDepotId(
      storedUserInfo?.depot_id ?? storedUserInfo?.depot?.depot_id,
    );
    const depotId = getPreferenceDepotId(selectedPreference, fallbackDepotId);
    if (depotId === null || depotId === undefined || depotId === '') {
      setNotificationPrefsError('Notification preference depot scope is missing.');
      return;
    }

    const nextPreferences = currentPreferences.map((pref) =>
      String(pref?.toggle_key ?? buildPreferenceToggleKey(pref)) === toggleKey
        ? {
            ...pref,
            is_enabled: checked,
          }
        : pref,
    );

    setNotificationPrefs(nextPreferences);
    setNotificationPrefsError('');
    setNotificationPrefsSuccess('');
    setNotificationPrefsSaving(true);

    API.patch('settings/update-notification-preferences', {
      notification_preference_id: preferenceId,
      is_enabled: checked,
      depot_id: depotId,
    })
      .then((response) => {
        const updatedPreference = response?.data?.data;
        const updatedAt = updatedPreference?.updated_at || new Date().toISOString();
        const updatedDepotId = getPreferenceDepotId(updatedPreference, depotId);
        setNotificationPrefs((previous) =>
          previous.map((pref) =>
            String(pref?.toggle_key ?? buildPreferenceToggleKey(pref)) === toggleKey
              ? {
                  ...pref,
                  is_enabled: checked,
                  updated_at: updatedAt,
                  depot_id: updatedDepotId,
                  scopes: Array.isArray(updatedPreference?.scopes)
                    ? updatedPreference.scopes
                    : pref.scopes,
                }
              : pref,
          ),
        );
        setNotificationPrefsSuccess('Notification preferences updated.');
      })
      .catch((error) => {
        console.error('Error updating notification preferences:', error);
        setNotificationPrefs(currentPreferences);
        setNotificationPrefsError('Failed to update notification preferences. Please try again.');
      })
      .finally(() => {
        setNotificationPrefsSaving(false);
      });
  };

  const handleUiPreferenceChange = (event) => {
    const { name, value } = event.target;
    setUiPrefs((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCompactModeToggle = (event) => {
    setUiPrefs((previous) => ({
      ...previous,
      compactTables: event.target.checked,
    }));
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveMessage('');

    const payload = {
      firstName: profileValues.firstName.trim(),
      lastName: profileValues.lastName.trim(),
      username: profileValues.username,
      email: profileValues.email,
      phoneNumber: profileValues.phoneNumber.trim(),
      jobTitle: profileValues.jobTitle.trim(),
    };

    try {
      await API.post('settings/user', payload);
      setInitialProfileValues(payload);
      setSaveMessage('Profile information saved successfully.');

      const storedUserInfo = readStoredUserInfo();
      localStorage.setItem(
        'user_info',
        JSON.stringify({
          ...storedUserInfo,
          given_name: payload.firstName,
          family_name: payload.lastName,
          phone_number: payload.phoneNumber,
          position: payload.jobTitle,
        }),
      );
    } catch (error) {
      console.error('Error saving user information:', error);
      setSaveMessage('Failed to save profile information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetProfile = () => {
    setProfileValues(initialProfileValues);
    setSaveMessage('');
  };

  const handleDataExport = () => {
    const blob = new Blob([JSON.stringify({ profileValues, notificationPrefs, uiPrefs }, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'honeybadger-settings-export.json';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const releaseDataExportUrl = useCallback(() => {
    setDataExportDownloadUrl((previousUrl) => {
      if (previousUrl && String(previousUrl).startsWith('blob:')) {
        window.URL.revokeObjectURL(previousUrl);
      }
      return '';
    });
  }, []);

  useEffect(
    () => () => {
      releaseDataExportUrl();
    },
    [releaseDataExportUrl],
  );

  const generateFullDataExportPreview = useCallback(async () => {
    setDataExportError('');
    setDataExportUrlCopied(false);
    setDataExportLoading(true);

    try {
      const [bundleResponse, manifestResponse, firstLastInsertResponse] = await Promise.allSettled([
        API.get('settings/report-export/bundle'),
        API.get('settings/report-export/manifest'),
        API.get('settings/first-last-insert'),
      ]);

      if (bundleResponse.status !== 'fulfilled') {
        throw bundleResponse.reason;
      }

      const bundlePayload = bundleResponse?.value?.data || {};
      const manifestPayload =
        manifestResponse.status === 'fulfilled' ? manifestResponse?.value?.data || {} : {};
      const firstLastInsertPayload =
        firstLastInsertResponse.status === 'fulfilled'
          ? firstLastInsertResponse?.value?.data || {}
          : {};
      const bundleDatasets = bundlePayload?.datasets || {};
      const manifestDatasets = Array.isArray(manifestPayload?.datasets)
        ? manifestPayload.datasets
        : [];

      const tables = buildExportTables(manifestDatasets, bundleDatasets);
      const totalRows = tables.reduce((sum, table) => sum + toPositiveNumber(table.row_count), 0);
      const globalDateRange = findDateRange(
        tables.flatMap((table) => [table.first_inserted_at, table.last_inserted_at]),
      );
      const firstLastData = firstLastInsertPayload?.data || {};
      const hasFirstLastInsertData = firstLastInsertResponse.status === 'fulfilled';
      const firstInsertedAt =
        hasFirstLastInsertData
          ? (firstLastData?.first_latency_created_at ?? firstLastData?.first_inserted_at ?? null)
          : globalDateRange.first;
      const lastInsertedAt =
        hasFirstLastInsertData
          ? (firstLastData?.last_latency_created_at ?? firstLastData?.last_inserted_at ?? null)
          : globalDateRange.last;
      const estimatedCharge = calculateExportCharge(totalRows);
      const nowIso = new Date().toISOString();

      const exportPackage = {
        generated_at: nowIso,
        source: {
          bundle_endpoint: '/api/v1/settings/report-export/bundle',
          manifest_endpoint: '/api/v1/settings/report-export/manifest',
          first_last_insert_endpoint: '/api/v1/settings/first-last-insert',
        },
        manifest: manifestPayload,
        bundle: bundlePayload,
        first_last_insert: firstLastInsertPayload,
      };

      const blob = new Blob([JSON.stringify(exportPackage, null, 2)], {
        type: 'application/json',
      });

      releaseDataExportUrl();
      const endpointDownloadUrl = extractBundleDownloadUrl(bundlePayload, manifestPayload);
      setDataExportDownloadUrl(endpointDownloadUrl);

      setDataExportSummary({
        companyId: bundlePayload?.company_id || manifestPayload?.company_id || null,
        firstInsertedAt,
        lastInsertedAt,
        totalRows,
        dataSizeBytes: blob.size,
        estimatedChargeUsd: estimatedCharge,
        tables,
        generatedAt: nowIso,
        coverage: bundlePayload?.message || 'Full report export bundle',
      });
    } catch (error) {
      console.error('Error preparing full data export preview:', error);
      setDataExportSummary(null);
      setDataExportError('Failed to load report export bundle.');
      releaseDataExportUrl();
    } finally {
      setDataExportLoading(false);
    }
  }, [releaseDataExportUrl]);

  const handleOpenFullDataExportModal = () => {
    setOpenDataExportModal(true);
    setDataExportCarouselPage(0);
    generateFullDataExportPreview();
  };

  const handleCloseFullDataExportModal = () => {
    setOpenDataExportModal(false);
    setDataExportCarouselPage(0);
    setDataExportLoading(false);
    setDataExportError('');
    setDataExportUrlCopied(false);
    setDataExportSummary(null);
    releaseDataExportUrl();
  };

  const handlePreviousExportTableCards = () => {
    setDataExportCarouselPage((previous) => Math.max(previous - 1, 0));
  };

  const handleNextExportTableCards = () => {
    setDataExportCarouselPage((previous) =>
      Math.min(previous + 1, Math.max(dataExportTablePages.length - 1, 0)),
    );
  };

  const handleCopyDataExportUrl = async () => {
    if (!dataExportDownloadUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(dataExportDownloadUrl);
      setDataExportUrlCopied(true);
    } catch (error) {
      console.error('Failed to copy export URL:', error);
      setDataExportError('Unable to copy download URL to clipboard.');
    }
  };

  const resetSupportTicketForm = () => {
    setSupportTicket({ ...INITIAL_SUPPORT_TICKET });
    setSupportTicketErrors({});
    setSupportSubmitSuccess('');
    setSupportSubmitError('');
    setSupportSubmitting(false);
  };

  const handleOpenSupportModal = () => {
    resetSupportTicketForm();
    setOpenSupportModal(true);
  };

  const handleCloseSupportModal = () => {
    setOpenSupportModal(false);
    resetSupportTicketForm();
  };

  const validateSupportTicket = (ticket) => {
    const errors = {};
    if (!ticket.title.trim()) {
      errors.title = 'A brief title is required.';
    } else if (ticket.title.trim().length < 5) {
      errors.title = 'Use at least 5 characters.';
    }

    if (!ticket.description.trim()) {
      errors.description = 'Please describe your request.';
    } else if (ticket.description.trim().length < 20) {
      errors.description = 'Use at least 20 characters for better context.';
    }

    if (!ticket.priority) {
      errors.priority = 'Select a priority.';
    }

    if (!ticket.category) {
      errors.category = 'Select a category.';
    }

    return errors;
  };

  const handleSupportFieldChange = (event) => {
    const { name, value } = event.target;
    setSupportTicket((previous) => ({
      ...previous,
      [name]: value,
    }));
    setSupportSubmitSuccess('');
    setSupportSubmitError('');
    setSupportTicketErrors((previous) => ({
      ...previous,
      [name]: '',
    }));
  };

  const handleSupportTicketSubmit = async (event) => {
    event.preventDefault();
    const normalizedTicket = {
      ...supportTicket,
      title: supportTicket.title.trim(),
      description: supportTicket.description.trim(),
    };
    const validationErrors = validateSupportTicket(normalizedTicket);
    setSupportTicketErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setSupportSubmitting(true);
    setSupportSubmitSuccess('');
    setSupportSubmitError('');
    try {
      const payload = {
        title: normalizedTicket.title,
        message: normalizedTicket.description,
        priority: toTitleCase(normalizedTicket.priority),
        category: normalizedTicket.category,
      };

      await API.post('settings/support', payload);

      setSupportSubmitSuccess('Support request submitted successfully.');
      setSupportTicket({ ...INITIAL_SUPPORT_TICKET });
      setSupportTicketErrors({});
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSupportSubmitError('Failed to submit support request. Please try again.');
      setSupportTicket(normalizedTicket);
    } finally {
      setSupportSubmitting(false);
    }
  };

  const handleOpenPasswordResetModal = () => {
    setOpenPasswordResetModal(true);
  };

  const handleClosePasswordResetModal = () => {
    setOpenPasswordResetModal(false);
  };

  return (
    <>
      <Helmet>
        <title> Dashboard: Settings </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={0.8} sx={{ mb: 3 }}>
          <Typography variant="h4">Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage profile, security, notifications, and workspace preferences.
          </Typography>
        </Stack>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="h6">Profile</Typography>
                  <Chip size="small" label={hasProfileChanges ? 'Unsaved changes' : 'Saved'} color={hasProfileChanges ? 'warning' : 'success'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Update personal details used across reports, approvals, and notifications.
                </Typography>

                <Box component="form" noValidate onSubmit={handleProfileSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField required fullWidth label="First Name" name="firstName" value={profileValues.firstName} onChange={handleProfileFieldChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField required fullWidth label="Last Name" name="lastName" value={profileValues.lastName} onChange={handleProfileFieldChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField required fullWidth label="Username" name="username" value={profileValues.username} disabled />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField required fullWidth label="Email Address" name="email" value={profileValues.email} disabled />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField required fullWidth label="Phone Number" name="phoneNumber" value={profileValues.phoneNumber} onChange={handleProfileFieldChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField required fullWidth label="Position / Job Title" name="jobTitle" value={profileValues.jobTitle} disabled />
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={1.2} justifyContent="flex-end" sx={{ mt: 2.2 }}>
                    <Button variant="outlined" onClick={handleResetProfile} disabled={!hasProfileChanges || saving}>
                      Reset
                    </Button>
                    <Button type="submit" variant="contained" disabled={!hasProfileChanges || saving}>
                      {saving ? 'Saving...' : 'Save profile'}
                    </Button>
                  </Stack>
                </Box>

                {!!saveMessage && (
                  <Alert
                    sx={{ mt: 2 }}
                    severity={saveMessage.toLowerCase().includes('failed') ? 'error' : 'success'}
                  >
                    {saveMessage}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Notification Preferences
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Decide which updates are sent to your email and in-app feed.
                </Typography>

                {notificationPrefsLoading ? (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {Array.isArray(notificationPrefs) &&
                      notificationPrefs.map((pref) => {
                        const toggleKey = pref?.toggle_key ?? buildPreferenceToggleKey(pref);
                        return (
                        <Box
                          key={`notification-pref-${toggleKey || pref.id || pref.code}`}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            p: 1.3,
                            backgroundColor: '#f8fafc',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1.2}>
                            <Box sx={{ pr: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {pref.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {pref.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.7 }}>
                                Last updated: {formatUpdatedAt(pref.updated_at)}
                              </Typography>
                            </Box>
                            <Switch
                              name={String(toggleKey)}
                              checked={Boolean(pref.is_enabled)}
                              onChange={(event) => handleNotificationToggle(event, toggleKey)}
                              disabled={notificationPrefsSaving}
                            />
                          </Stack>
                        </Box>
                        );
                      })}
                  </Stack>
                )}

                {notificationPrefsSaving && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Saving preference update...
                    </Typography>
                  </Stack>
                )}

                {!!notificationPrefsError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    {notificationPrefsError}
                  </Alert>
                )}

                {!!notificationPrefsSuccess && (
                  <Alert severity="success" sx={{ mt: 1.5 }}>
                    {notificationPrefsSuccess}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Workspace Preferences
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Personalize how data and dates are shown in your workspace.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField select fullWidth label="Timezone" name="timezone" value={uiPrefs.timezone} onChange={handleUiPreferenceChange} disabled>
                      <MenuItem value="Africa/Johannesburg">Africa/Johannesburg</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="Europe/London">Europe/London</MenuItem>
                    </CustomTextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField select fullWidth label="Language" name="language" value={uiPrefs.language} onChange={handleUiPreferenceChange} disabled>
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                    </CustomTextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField select fullWidth label="Date Format" name="dateFormat" value={uiPrefs.dateFormat} onChange={handleUiPreferenceChange} disabled>
                      <MenuItem value="dd/MM/yyyy">dd/MM/yyyy</MenuItem>
                      <MenuItem value="MM/dd/yyyy">MM/dd/yyyy</MenuItem>
                      <MenuItem value="yyyy-MM-dd">yyyy-MM-dd</MenuItem>
                    </CustomTextField>
                  </Grid>
                </Grid>

                <FormControlLabel
                  sx={{ mt: 1.5 }}
                  control={<Switch checked={uiPrefs.compactTables} onChange={handleCompactModeToggle} disabled />}
                  label="Use compact table density"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                  Workspace preferences are temporarily disabled.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Account Snapshot
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body2" fontWeight={600}>{accountMeta.name || '-'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body2" fontWeight={600}>{accountMeta.email || profileValues.email || '-'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Role</Typography>
                    <Typography variant="body2" fontWeight={600}>{accountMeta.role || '-'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Company</Typography>
                    <Typography variant="body2" fontWeight={600}>{accountMeta.companyName || '-'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Company ID</Typography>
                    <Typography variant="body2" fontWeight={600}>{accountMeta.companyId || '-'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Registration ID</Typography>
                    <Typography variant="body2" fontWeight={600}>{accountMeta.registrationId || '-'}</Typography>
                  </Stack>
                </Stack>

                {!!accountMetaError && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    {accountMetaError}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Security
                </Typography>
                <Stack spacing={1.1}>
                  <Button fullWidth variant="outlined" onClick={handleOpenPasswordResetModal}>
                    Change password
                  </Button>
                  <Button fullWidth variant="outlined" disabled>
                    Configure multi-factor authentication
                  </Button>
                  <Button fullWidth color="warning" variant="outlined" disabled>
                    Sign out all devices
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                  Multi-factor authentication and session revocation are temporarily disabled.
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Data & Support
                </Typography>
                <Stack spacing={1.1}>
                  <Button fullWidth variant="outlined" onClick={handleDataExport}>
                    Export settings JSON
                  </Button>
                  <Button fullWidth variant="outlined" onClick={handleOpenFullDataExportModal}>
                    Request full data export
                  </Button>
                  <Button fullWidth variant="outlined" onClick={handleOpenSupportModal}>
                    Contact support
                  </Button>
                </Stack>

                <Divider sx={{ my: 1.8 }} />
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  onClick={() => alert('Account deactivation request flow is not connected yet.')}
                >
                  Request account deactivation
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <ForgotPassword
        open={openPasswordResetModal}
        handleClose={handleClosePasswordResetModal}
        title="Reset Password"
      />

      <Dialog
        open={openDataExportModal}
        onClose={handleCloseFullDataExportModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Full Data Export</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              This preview uses report-export bundle + manifest endpoints, supports dynamic datasets,
              and prices exports by total row count.
            </Alert>

            {dataExportError && <Alert severity="error">{dataExportError}</Alert>}

            {dataExportLoading && (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {dataExportSummary && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label={`Company ID: ${dataExportSummary.companyId || '-'}`} size="small" color="primary" variant="outlined" />
                  <Chip label={`Datasets: ${integerFormatter.format(dataExportSummary.tables.length)}`} size="small" variant="outlined" />
                  <Chip label={`Generated: ${formatExportDateTime(dataExportSummary.generatedAt)}`} size="small" variant="outlined" />
                </Stack>

                <Grid container spacing={1.5}>
                  {dataExportCards.map((summaryCard) => {
                    const SummaryIcon = summaryCard.icon;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={`export-summary-${summaryCard.key}`}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.3,
                                display: 'grid',
                                placeItems: 'center',
                                backgroundColor: '#eff6ff',
                                color: 'primary.main',
                                flexShrink: 0,
                              }}
                            >
                              <SummaryIcon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary">
                                {summaryCard.label}
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.65, fontWeight: 700, wordBreak: 'break-word' }}>
                                {summaryCard.value}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 1, backgroundColor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">Table Coverage & Row Counts</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.2}>
                        <Typography variant="caption" color="text.secondary">
                          {`${dataExportTablePages.length > 0 ? currentDataExportPageIndex + 1 : 0}/${dataExportTablePages.length || 0}`}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={handlePreviousExportTableCards}
                          disabled={currentDataExportPageIndex === 0 || dataExportTablePages.length === 0}
                        >
                          <ChevronLeftRounded fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleNextExportTableCards}
                          disabled={
                            dataExportTablePages.length === 0
                            || currentDataExportPageIndex >= dataExportTablePages.length - 1
                          }
                        >
                          <ChevronRightRounded fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Grid container spacing={1.5}>
                      {visibleDataExportTables.map((table) => (
                        <Grid item xs={12} sm={6} md={4} key={`export-table-${table.table_name}`}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 1.4, '&:last-child': { pb: 1.4 } }}>
                              <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {humanizeDatasetName(table.table_name)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={table.is_loaded ? 'Loaded' : 'Pending'}
                                    color={table.is_loaded ? 'success' : 'default'}
                                    variant={table.is_loaded ? 'filled' : 'outlined'}
                                  />
                                </Stack>

                                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Rows ${integerFormatter.format(table.row_count)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Loaded ${integerFormatter.format(table.loaded_rows)}`}
                                  />
                                </Stack>

                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    First insert
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatExportDateTime(table.first_inserted_at)}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Last insert
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatExportDateTime(table.last_inserted_at)}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                      {visibleDataExportTables.length === 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            No dataset cards available for this export response.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>

                <Stack spacing={1}>
                  <CustomTextField
                    fullWidth
                    label="Download URL"
                    value={dataExportDownloadUrl}
                    disabled
                    helperText={
                      dataExportDownloadUrl
                        ? 'URL returned by export endpoint.'
                        : 'No download link returned in the bundle response.'
                    }
                  />
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleCopyDataExportUrl} disabled={!dataExportDownloadUrl}>
                      {dataExportUrlCopied ? 'URL copied' : 'Copy URL'}
                    </Button>
                    <Button
                      variant="contained"
                      component="a"
                      href={dataExportDownloadUrl || undefined}
                      download={`honeybadger-full-data-export-${new Date().toISOString().slice(0, 10)}.json`}
                      disabled={!dataExportDownloadUrl}
                    >
                      Download package
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Pricing model: {usdFormatter.format(DATA_EXPORT_ROW_RATE_USD)} per row.
                  </Typography>
                </Stack>
              </>
            )}

            {!dataExportLoading && !dataExportSummary && !dataExportError && (
              <Typography variant="body2" color="text.secondary">
                Click refresh preview to load the latest export manifest and bundle.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.8 }}>
          <Button onClick={handleCloseFullDataExportModal}>Close</Button>
          <Button variant="outlined" onClick={() => generateFullDataExportPreview()} disabled={dataExportLoading}>
            {dataExportLoading ? 'Refreshing...' : 'Refresh preview'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSupportModal}
        onClose={handleCloseSupportModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Contact support</DialogTitle>
        <Box component="form" onSubmit={handleSupportTicketSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Alert severity="info">
                Include clear business impact and steps to reproduce if this is a bug.
              </Alert>

              <CustomTextField
                required
                fullWidth
                name="title"
                label="Brief title"
                value={supportTicket.title}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.title)}
                helperText={supportTicketErrors.title || 'Example: Fuel sales chart missing for March 2026'}
                inputProps={{ maxLength: 120 }}
              />

              <CustomTextField
                required
                fullWidth
                multiline
                minRows={4}
                name="description"
                label="Describe your request"
                value={supportTicket.description}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.description)}
                helperText={
                  supportTicketErrors.description ||
                  `${supportTicket.description.length}/2000 characters`
                }
                inputProps={{ maxLength: 2000 }}
              />

              <CustomTextField
                required
                select
                fullWidth
                name="priority"
                label="Priority"
                value={supportTicket.priority}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.priority)}
                helperText={supportTicketErrors.priority || 'Choose urgency based on business impact.'}
              >
                {SUPPORT_PRIORITY_OPTIONS.map((priorityOption) => (
                  <MenuItem key={`support-priority-${priorityOption.value}`} value={priorityOption.value}>
                    {priorityOption.label}
                  </MenuItem>
                ))}
              </CustomTextField>

              <CustomTextField
                required
                select
                fullWidth
                name="category"
                label="Category"
                value={supportTicket.category}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.category)}
                helperText={supportTicketErrors.category || 'Select the single category that best describes the request.'}
              >
                {SUPPORT_TAG_OPTIONS.map((tagOption) => (
                  <MenuItem key={`support-category-${tagOption}`} value={tagOption}>
                    {tagOption}
                  </MenuItem>
                ))}
              </CustomTextField>

              {!!supportSubmitError && (
                <Alert severity="error">
                  {supportSubmitError}
                </Alert>
              )}

              {!!supportSubmitSuccess && (
                <Alert severity="success">
                  {supportSubmitSuccess}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.8 }}>
            <Button onClick={handleCloseSupportModal} disabled={supportSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={supportSubmitting}>
              {supportSubmitting ? 'Submitting...' : 'Submit request'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
